import { useCallback, useState } from 'react';
import type { ChangeEvent, SyntheticEvent } from 'react';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';

import { EMPTY_STRING } from '@e-socna/empty';
import type { ResolverId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { stopPropagation } from '../../../../utils/stop-propagation.util';

import {
  ALWAYS_DISPLAYED,
  displayConditionChoiceOf,
  displayConditionOf,
  NEVER_DISPLAYED,
} from '../../utils/display-condition-choice.util';
import type { DocumentEditorCmp_SectionDisplayCondition } from '../../fragments/recursive-section.fragment';

enum DisplayMode {
  Yes = 'yes',
  No = 'no',
  Resolver = 'resolver',
}

interface Props {
  readonly displayCondition:
    DocumentEditorCmp_SectionDisplayCondition | null | undefined;
  readonly isOpen: boolean;
  readonly onClose: (event: Readonly<SyntheticEvent>) => void;
  readonly onValidate: (
    displayCondition: DocumentEditorCmp_SectionDisplayCondition | null,
  ) => void;
}

const displayModeOf = (
  choice: ReturnType<typeof displayConditionChoiceOf>,
): DisplayMode => {
  switch (choice) {
    case ALWAYS_DISPLAYED:
      return DisplayMode.Yes;
    case NEVER_DISPLAYED:
      return DisplayMode.No;
    default:
      return DisplayMode.Resolver;
  }
};

export const DisplayConditionDialog = ({
  displayCondition,
  isOpen,
  onClose: handleClose,
  onValidate,
}: Readonly<Props>) => {
  const { resolvers, resolverReturnTypesCodeMap } = useReferencesContext();

  const initialChoice = displayConditionChoiceOf(displayCondition);
  const [mode, setMode] = useState(() => displayModeOf(initialChoice));
  const [resolverId, setResolverId] = useState<ResolverId | null>(() =>
    displayModeOf(initialChoice) === DisplayMode.Resolver
      ? (initialChoice as ResolverId)
      : null,
  );

  const booleanResolvers = resolvers.filter(
    (resolver) =>
      resolver.isResolvableOnFront &&
      resolver.returnTypeId === resolverReturnTypesCodeMap.Boolean._id,
  );

  const handleModeChange = useCallback(
    (_event: Readonly<ChangeEvent<HTMLInputElement>>, value: string) => {
      setMode(value as DisplayMode);
    },
    [],
  );

  const handleResolverChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      setResolverId(event.target.value as ResolverId);
    },
    [],
  );

  const isResolverMissing = mode === DisplayMode.Resolver && !resolverId;

  const handleValidate = useCallback(
    (event: Readonly<SyntheticEvent>) => {
      if (mode === DisplayMode.Resolver) {
        if (resolverId) {
          onValidate(displayConditionOf(resolverId));
        }
      } else {
        onValidate(
          displayConditionOf(
            mode === DisplayMode.Yes ? ALWAYS_DISPLAYED : NEVER_DISPLAYED,
          ),
        );
      }

      handleClose(event);
    },
    [handleClose, mode, onValidate, resolverId],
  );

  return (
    // Rendered from the section header: its clicks must not fold the section.
    <Dialog
      fullWidth
      maxWidth="sm"
      onClick={stopPropagation}
      onClose={handleClose}
      open={isOpen}
    >
      <DialogTitle>Condition d&apos;affichage</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Une section qui n&apos;est pas affichée n&apos;est ni numérotée, ni
          listée dans la table des matières.
        </DialogContentText>
        <FormControl>
          <FormLabel id="section-display-condition-label">
            Afficher la section
          </FormLabel>
          <RadioGroup
            aria-labelledby="section-display-condition-label"
            onChange={handleModeChange}
            row
            value={mode}
          >
            <FormControlLabel
              control={<Radio />}
              label="Oui"
              value={DisplayMode.Yes}
            />
            <FormControlLabel
              control={<Radio />}
              label="Non"
              value={DisplayMode.No}
            />
            <FormControlLabel
              control={<Radio />}
              label="Résolveur"
              value={DisplayMode.Resolver}
            />
          </RadioGroup>
        </FormControl>
        <TextField
          disabled={mode !== DisplayMode.Resolver}
          fullWidth
          id="section-display-condition-resolver"
          label="Résolveur booléen"
          onChange={handleResolverChange}
          required={mode === DisplayMode.Resolver}
          select
          sx={{ mt: 2 }}
          value={resolverId ?? EMPTY_STRING}
        >
          {booleanResolvers.map((resolver) => (
            <MenuItem key={resolver._id} value={resolver._id}>
              {resolver.code}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={handleClose} size="large" variant="outlined">
          Annuler
        </Button>
        <Button
          disabled={isResolverMissing}
          onClick={handleValidate}
          size="large"
          variant="contained"
        >
          Valider
        </Button>
      </DialogActions>
    </Dialog>
  );
};
